import React from "react";
import "./AddNodeToSection.css";

export interface NodeCandidate {
  id: number;
  name: string;
  /** The node's current Takt section ("" = unmapped). */
  section: string;
}

interface Props {
  /** Every task-holding node NOT already in this section. */
  candidates: NodeCandidate[];
  disabled: boolean;
  onPick: (nodeId: number) => void;
}

/** Per-section "＋ Add WBS node" picker: a searchable list of every task-holding
 *  node (each showing its current section, or "unmapped"); picking one re-files
 *  it into this section. */
const AddNodeToSection: React.FC<Props> = ({ candidates, disabled, onPick }) => {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");

  const close = () => {
    setOpen(false);
    setQ("");
  };

  const filtered = React.useMemo(() => {
    const query = q.trim().toLowerCase();
    const list = query
      ? candidates.filter(
          (c) =>
            c.name.toLowerCase().includes(query) ||
            c.section.toLowerCase().includes(query),
        )
      : candidates;
    return list.slice(0, 100);
  }, [candidates, q]);

  if (!open) {
    return (
      <button
        type="button"
        className="wbsmap-addnode-btn"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        ＋ Add WBS node
      </button>
    );
  }

  return (
    <div className="wbsmap-addnode">
      <div className="wbsmap-addnode-bar">
        <input
          className="wbsmap-addnode-input"
          autoFocus
          value={q}
          placeholder="Search WBS nodes…"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") close();
          }}
        />
        <button type="button" className="wbsmap-addnode-x" onClick={close} title="Close">
          ✕
        </button>
      </div>
      <div className="wbsmap-addnode-list">
        {filtered.length === 0 ? (
          <div className="wbsmap-addnode-none">No matching nodes.</div>
        ) : (
          filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              className="wbsmap-addnode-opt"
              onClick={() => {
                onPick(c.id);
                close();
              }}
            >
              <span className="wbsmap-addnode-opt-name" title={c.name}>
                {c.name}
              </span>
              <span className={`wbsmap-addnode-opt-sec ${c.section ? "" : "is-unmapped"}`}>
                {c.section ? `in: ${c.section}` : "unmapped"}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default AddNodeToSection;
